import { useState, useEffect } from 'react'
import { useCurrency } from '../../util/CurrencyContext'
import { FormInput, FormSelect } from '../../shared/components/Fieldset'
import { FaChevronDown, FaChevronUp, FaCalculator } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

export default function ZakahCalculator({ onResultChange }) {
    const [cash, setCash] = useState('')
    const [goldGrams, setGoldGrams] = useState('')
    const [silverGrams, setSilverGrams] = useState('')
    const [goldPrice, setGoldPrice] = useState(100)
    const [silverPrice, setSilverPrice] = useState(1.2)
    const [nisabType, setNisabType] = useState('gold')
    const [isOpen, setIsOpen] = useState(true)
    const [zakahDue, setZakahDue] = useState(0)
    const [isCalculated, setIsCalculated] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const { currency, currencyRate, currencySymbol } = useCurrency()
    const { t } = useTranslation()

    useEffect(() => {
        const fetchPrices = async () => {
            setIsLoading(true)
            try {
                const [goldRes, silverRes] = await Promise.all([
                    fetch('https://api.gold-api.com/price/XAU'),
                    fetch('https://api.gold-api.com/price/XAG'),
                ])
                const goldData = await goldRes.json()
                const silverData = await silverRes.json()

                const usdToLocalRes = await fetch(
                    `https://api.frankfurter.app/latest?from=USD&to=GBP`
                )
                const usdToLocalData = await usdToLocalRes.json()
                const usdToGbp = usdToLocalData.rates.GBP

                setGoldPrice((goldData.price / 31.1035) * usdToGbp)
                setSilverPrice((silverData.price / 31.1035) * usdToGbp)
            } catch (err) {
                console.error('Failed to fetch gold/silver price', err)
            } finally {
                setIsLoading(false)
            }
        }

        fetchPrices()
    }, [currency, currencyRate])

    const calculateZakah = () => {
        const cashValue = parseFloat(cash) || 0
        const goldValue = (parseFloat(goldGrams) || 0) * goldPrice
        const silverValue = (parseFloat(silverGrams) || 0) * silverPrice
        const totalAssets = cashValue + goldValue + silverValue

        const nisabWeight = nisabType === 'gold' ? 85 : 595
        const nisabPrice = nisabType === 'gold' ? goldPrice : silverPrice
        const nisabValue = nisabWeight * nisabPrice

        const calculatedZakah = totalAssets >= nisabValue ? totalAssets * 0.025 : 0
        const finalAmount = Number(calculatedZakah.toFixed(2))

        setZakahDue(finalAmount)
        setIsCalculated(true)
        onResultChange(finalAmount)
    }

    const resetCalculator = () => {
        setCash('')
        setGoldGrams('')
        setSilverGrams('')
        setZakahDue(0)
        setIsCalculated(false)
        onResultChange(0)
    }

    const formatNumber = num => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num)
    }

    return (
        <div
            className={`p-5 rounded-xl border-2 transition-all duration-300 ${
                isCalculated && zakahDue > 0
                    ? 'border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 shadow-md'
                    : 'border-amber-100 bg-gradient-to-br from-amber-50/50 to-white'
            }`}
        >
            {/* Header */}
            <div
                className='flex items-center justify-between mb-4 cursor-pointer group'
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className='flex items-center gap-3'>
                    <div className='p-2 rounded-lg bg-gradient-to-br from-emerald-100 to-green-100'>
                        <FaCalculator className='text-lg text-emerald-600' />
                    </div>
                    <div>
                        <h3 className='text-lg font-semibold text-gray-900'>
                            {t('religious-dues.zakat.title')}
                        </h3>
                        <p className='text-sm text-gray-600'>{t('donation.zakah.title')}</p>
                    </div>
                </div>
                <button className='p-2 text-gray-600 transition-all duration-300 rounded-lg hover:bg-amber-100 group-hover:text-amber-600'>
                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
            </div>

            {isOpen && (
                <div className='space-y-5'>
                    {/* Nisab Type Selector */}
                    <div className='space-y-2'>
                        <label className='block text-sm font-medium text-gray-700'>
                            {t('religious-dues.zakat.nisab.title')}
                        </label>
                        <div className='flex gap-3'>
                            {[
                                { label: t('religious-dues.zakat.nisab.gold'), value: 'gold' },
                                { label: t('religious-dues.zakat.nisab.silver'), value: 'silver' },
                            ].map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => setNisabType(option.value)}
                                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                                        nisabType === option.value
                                            ? 'border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50 text-gray-900 font-semibold'
                                            : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-700'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <p className='text-xs text-gray-500'>
                            {/* {t('donation.secure')} */}

                            {t('donation.zakah.usingNisab', {
                                type:
                                    nisabType === 'gold'
                                        ? t('donation.zakah.gold')
                                        : t('donation.zakah.silver'),
                                amount: nisabType === 'gold' ? 85 : 595,
                            })}
                        </p>
                    </div>

                    {/* Input Fields */}
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                        <div className='space-y-2'>
                            <label className='block text-sm font-medium text-gray-700'>
                                {t('religious-dues.zakat.gold')} ({t('donation.zakah.gram')})
                            </label>
                            <div className='relative'>
                                <input
                                    type='number'
                                    value={goldGrams}
                                    onChange={e => setGoldGrams(e.target.value)}
                                    placeholder='0'
                                    className='w-full py-3 pl-10 pr-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent'
                                    min='0'
                                    step='0.01'
                                />
                                <div className='absolute text-gray-500 transform -translate-y-1/2 left-3 top-1/2'>
                                    <svg
                                        className='w-4 h-4'
                                        fill='currentColor'
                                        viewBox='0 0 20 20'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z'
                                            clipRule='evenodd'
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className='space-y-2'>
                            <label className='block text-sm font-medium text-gray-700'>
                                {t('religious-dues.zakat.silver')} ({t('donation.zakah.gram')})
                            </label>
                            <div className='relative'>
                                <input
                                    type='number'
                                    value={silverGrams}
                                    onChange={e => setSilverGrams(e.target.value)}
                                    placeholder='0'
                                    className='w-full py-3 pl-10 pr-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent'
                                    min='0'
                                    step='0.01'
                                />
                                <div className='absolute text-gray-500 transform -translate-y-1/2 left-3 top-1/2'>
                                    <svg
                                        className='w-4 h-4'
                                        fill='currentColor'
                                        viewBox='0 0 20 20'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            d='M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z'
                                            clipRule='evenodd'
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className='space-y-2 md:col-span-2'>
                            <label className='block text-sm font-medium text-gray-700'>
                                {t('religious-dues.zakat.savings')} {currency})
                            </label>
                            <div className='relative'>
                                <div className='absolute font-bold text-gray-700 transform -translate-y-1/2 left-3 top-1/2'>
                                    {currencySymbol}
                                </div>
                                <input
                                    type='number'
                                    value={cash}
                                    onChange={e => setCash(e.target.value)}
                                    placeholder='0'
                                    className='w-full py-3 pl-10 pr-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent'
                                    min='0'
                                    step='0.01'
                                />
                            </div>
                        </div>
                    </div>

                    {/* Current Prices */}
                    <div className='p-3 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100'>
                        <div className='flex items-center justify-between text-sm'>
                            <span className='text-gray-600'>{t('donation.zakah.goldPrice')}</span>
                            <span className='font-medium text-gray-900'>
                                {currencySymbol} {formatNumber(goldPrice)}/
                                {t('donation.zakah.gram')}
                            </span>
                        </div>
                        <div className='flex items-center justify-between mt-1 text-sm'>
                            <span className='text-gray-600'>{t('donation.zakah.silverPrice')}</span>
                            <span className='font-medium text-gray-900'>
                                {currencySymbol} {formatNumber(silverPrice)}/
                                {t('donation.zakah.gram')}
                            </span>
                        </div>
                    </div>

                    {/* Calculate Button */}
                    <div className='flex gap-3'>
                        <button
                            onClick={calculateZakah}
                            disabled={isLoading}
                            className='flex-1 py-3 px-4 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {isLoading ? (
                                <>
                                    <div className='w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin'></div>
                                    <span>{t('donation.zakah.fetchingPrices')}</span>
                                </>
                            ) : (
                                <>
                                    <FaCalculator className='w-5 h-5' />
                                    <span>{t('donation.zakah.calculate')}</span>
                                </>
                            )}
                        </button>

                        {isCalculated && (
                            <button
                                onClick={resetCalculator}
                                className='px-4 py-3 font-medium text-gray-700 transition-all duration-300 border border-gray-300 rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300'
                            >
                                {t('donation.zakah.reset')}
                            </button>
                        )}
                    </div>

                    {/* Zakat Result */}
                    {isCalculated && (
                        <div
                            className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                                zakahDue > 0
                                    ? 'border-green-500 bg-gradient-to-r from-green-50 to-emerald-50'
                                    : 'border-yellow-500 bg-gradient-to-r from-yellow-50 to-amber-50'
                            }`}
                        >
                            <div className='flex items-center justify-between'>
                                <div>
                                    <div className='flex items-center gap-2 mb-1'>
                                        <div
                                            className={`w-3 h-3 rounded-full ${
                                                zakahDue > 0 ? 'bg-green-500' : 'bg-yellow-500'
                                            }`}
                                        ></div>
                                        <h4 className='font-semibold text-gray-900'>
                                            {zakahDue > 0
                                                ? t('religious-dues.zakat.zakahDue')
                                                : 'Below Nisab'}
                                        </h4>
                                    </div>
                                    {zakahDue > 0 ? (
                                        <p className='text-sm text-gray-600'>
                                            {t('donation.zakah.above')}
                                        </p>
                                    ) : (
                                        <p className='text-sm text-gray-600'>
                                            {t('donation.zakah.below')}
                                        </p>
                                    )}
                                </div>
                                {zakahDue > 0 && (
                                    <div className='text-right'>
                                        <div className='text-2xl font-bold text-green-900'>
                                            {currencySymbol}
                                            {formatNumber(zakahDue)}
                                        </div>
                                        <div className='text-sm text-green-700'>({currency})</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Info Footer */}
                    <div className='pt-3 border-t border-gray-200'>
                        <p className='text-xs text-center text-gray-500'>
                            {t('donation.zakah.description')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
